import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import Flag from 'react-world-flags';
import DSBox from '../../../design-system/DSBox';
import DSTypography from '../../../design-system/Typography/DSTypography';
import DSMenu from '../../../design-system/DSMenu';
import DSMenuItem from '../../../design-system/DSMenuItem';
import useCountryDropdown from '../hooks/useCountryDropdown';
import { ReactComponent as ArrowDropDown } from '../../../design-system/Icons/ArrowDropDown.svg';

type CompactCountrySelectorProps = {
  selectedCountry: string;
  onCountryChange: (countryCode: string) => void;
};

const CompactCountrySelector = ({
  selectedCountry,
  onCountryChange,
}: CompactCountrySelectorProps) => {
  const { spacing } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { options, selectedCountry: currentCountry } = useCountryDropdown({
    initialCountryCode: selectedCountry,
    onSelect: onCountryChange,
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCountrySelect = (countryCode: string) => {
    onCountryChange(countryCode);
    handleClose();
  };

  return (
    <>
      <DSBox
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing(1),
          cursor: 'pointer',
          padding: spacing(0.5, 1),
          borderRadius: spacing(1),
        }}
      >
        <DSBox
          sx={{
            width: spacing(2.5),
            height: spacing(2.5),
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Flag
            code={currentCountry?.code || selectedCountry}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt={`${currentCountry?.code || selectedCountry} Flag`}
          />
        </DSBox>

        <DSTypography
          sx={{
            fontFamily: 'Gilroy-Medium',
            fontSize: spacing(1.75),
          }}
        >
          {(currentCountry?.code || selectedCountry).toUpperCase()}
        </DSTypography>

        <ArrowDropDown />
      </DSBox>

      <DSMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {options.map((country) => (
          <DSMenuItem
            key={country.code}
            onClick={() => handleCountrySelect(country.code)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing(1),
              minWidth: spacing(20),
            }}
          >
            <DSBox
              sx={{
                width: spacing(2.5),
                height: spacing(2.5),
                borderRadius: '50%',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Flag
                code={country.code}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                alt={`${country.code} Flag`}
              />
            </DSBox>
            <DSTypography sx={{ fontSize: spacing(1.75) }}>
              {country.code.toUpperCase()} - {country.name}
            </DSTypography>
          </DSMenuItem>
        ))}
      </DSMenu>
    </>
  );
};

export default CompactCountrySelector;
